import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

// Paths of the group feature child routes to keep alive between tab switches.
// The empty-path route ('') is intentionally excluded — caching the feature module
// root causes a circular reference in Angular's RouterStateSnapshot tree on reattach.
const CACHEABLE_PATHS = new Set(['staff', 'offices', 'documents', 'settings']);

@Injectable()
export class GroupTabReuseStrategy implements RouteReuseStrategy {
  private cache = new Map<string, DetachedRouteHandle>();
  private cachedGroupId: string | null = null;

  private getGroupId(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route.parent;
    while (current) {
      if (current.params['groupId']) return current.params['groupId'];
      current = current.parent;
    }
    return null;
  }

  private getKey(route: ActivatedRouteSnapshot): string | null {
    const path = route.routeConfig?.path;
    if (!path || !CACHEABLE_PATHS.has(path)) return null;
    const groupId = this.getGroupId(route);
    if (!groupId) return null;
    return `${groupId}::${path}`;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.getKey(route) !== null;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.getKey(route);
    if (!key) return;

    const groupId = this.getGroupId(route);
    if (groupId && groupId !== this.cachedGroupId) {
      this.cache.clear();
      this.cachedGroupId = groupId;
    }

    if (handle) {
      this.cache.set(key, handle);
    } else {
      this.cache.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getKey(route);
    return key !== null && this.cache.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.cache.get(this.getKey(route) ?? '') ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
